import { Dispatch, MouseEvent, MouseEventHandler, SetStateAction, useEffect, useState } from "react";
import { ResizeObserver } from "@juggle/resize-observer";
import ReactGridLayout from "react-grid-layout";
import { useWindowSize } from "go-core/components/useWindowSize";
import {
	DASHBOARD_GRID_LAYOUT_MARGIN_VALUE,
	DASHBOARD_GRID_LAYOUT_ROW_HEIGHT,
	REVERSED_ORDER_OF_DASHBOARD_MOBILE_ITEMS,
} from "../segments/constants";
import { DashboardGridItem } from "../types";
import useDashboardContext from "./useDashboardContext";

export const useDashboardDraggable = (
	dashboardGridItem: DashboardGridItem,
	setNonMobileLayout?: Dispatch<SetStateAction<ReactGridLayout.Layout[]>>,
	dashboardGridItemChildrenToBeObserved?: HTMLCollection,
	onMouseDown?: MouseEventHandler<HTMLDivElement>,
	onMouseUp?: MouseEventHandler<HTMLDivElement>
) => {
	const { isMobile } = useWindowSize();
	const {
		isEditMode,
		state: {
			dashboardEdit: { nonMobileGridConfig },
		},
	} = useDashboardContext();
	const [cursorType, setCursorType] = useState<React.CSSProperties["cursor"]>("default");

	const getWideDashboardGridItemIsUnderCurrentBoxElement = (
		dashboardGridElementConfig: ReactGridLayout.Layout
	): boolean => {
		const { y: currentGridItemYCoordinate } = dashboardGridElementConfig;
		const wideDashboardGridItem = nonMobileGridConfig.find(
			(dashboardgridItem) => dashboardgridItem.i === DashboardGridItem.SALES_ADVANCED
		);

		if (!wideDashboardGridItem || dashboardGridElementConfig.i === DashboardGridItem.SALES_ADVANCED) return false;

		const { y: wideDashboardGridItemYCoordinate } = wideDashboardGridItem;

		if (currentGridItemYCoordinate < wideDashboardGridItemYCoordinate) return true;

		return false;
	};

	const getGridItemZIndex = (): string => {
		if (isMobile) {
			const elementOrderNumber = REVERSED_ORDER_OF_DASHBOARD_MOBILE_ITEMS.indexOf(dashboardGridItem);

			return (elementOrderNumber + 1).toString();
		}

		const dashboardGridElementConfig = nonMobileGridConfig.find((gridItem) => gridItem.i === dashboardGridItem);
		if (!dashboardGridElementConfig) return "0";

		const { x: xCoordinate, y: yCoordinate } = dashboardGridElementConfig;

		const isWideDashboardGridItemUnderCurrentBoxElement =
			getWideDashboardGridItemIsUnderCurrentBoxElement(dashboardGridElementConfig);

		const gridLayoutYCoordinates = nonMobileGridConfig.map((gridItem) => gridItem.y);
		const uniqueGridLayoutYCoordinates = Array.from(new Set(gridLayoutYCoordinates));
		const sortedGridItemsByYCoordinate = uniqueGridLayoutYCoordinates.sort((a, b) => a - b);
		const gridItemPositionCountingFromTop = sortedGridItemsByYCoordinate.indexOf(yCoordinate);
		const gridItemPositionCountingFromBottom = Math.abs(
			gridItemPositionCountingFromTop - sortedGridItemsByYCoordinate.length
		);

		const xCoordinateCountingFromRight = Math.abs(xCoordinate - 3);
		const zIndexPartBasedOnXCoordinate = xCoordinateCountingFromRight * 10;

		let gridItemZIndex = gridItemPositionCountingFromBottom + zIndexPartBasedOnXCoordinate;

		if (isWideDashboardGridItemUnderCurrentBoxElement) {
			gridItemZIndex *= 2;
		}

		return gridItemZIndex.toString();
	};

	useEffect(() => {
		if (!isMobile && dashboardGridItemChildrenToBeObserved && setNonMobileLayout) {
			const resizeObserver = new ResizeObserver((entries) => {
				if (isEditMode) return;

				let dashboardGridItemTotalHeight = 0;

				for (const index in entries) {
					const elementHeight = entries[index].borderBoxSize[0].blockSize;
					let currentlyObservedGridItemChild: null | Element = null;

					for (const child of dashboardGridItemChildrenToBeObserved) {
						if (child === entries[index]?.target) {
							currentlyObservedGridItemChild = child;
						}
					}
					if (!currentlyObservedGridItemChild) {
						continue;
					}

					const entryStyle = window.getComputedStyle(currentlyObservedGridItemChild);
					const { marginTop, marginBottom } = entryStyle;
					const entryMarginTop = parseFloat(marginTop);
					const entryMarginBottom = parseFloat(marginBottom);
					let entryExtraSize = entryMarginTop + entryMarginBottom;

					const { parentElement } = currentlyObservedGridItemChild;
					if (parentElement && index === "0") {
						const parentStyle = window.getComputedStyle(parentElement);
						const { paddingTop, paddingBottom } = parentStyle;
						const parentPaddingTop = parseFloat(paddingTop);
						const parentPaddingBottom = parseFloat(paddingBottom);
						entryExtraSize += parentPaddingTop + parentPaddingBottom;
					}

					dashboardGridItemTotalHeight += elementHeight;
					dashboardGridItemTotalHeight += entryExtraSize;
				}

				const elementHPreciseValue =
					dashboardGridItemTotalHeight /
					(DASHBOARD_GRID_LAYOUT_ROW_HEIGHT + DASHBOARD_GRID_LAYOUT_MARGIN_VALUE);
				const elementHTruncatedValue = Math.trunc(elementHPreciseValue);
				const difference = elementHPreciseValue - elementHTruncatedValue;
				let newElementHValue = Math.ceil(elementHPreciseValue);

				if (difference > 0.9 || difference === 0) {
					newElementHValue++;
				}

				setNonMobileLayout((previousNonMobileLayout) => {
					const newNonMobileGridConfig = JSON.parse(JSON.stringify(previousNonMobileLayout));
					const gridItemToBeChanged = newNonMobileGridConfig.find(
						(gridItem: ReactGridLayout.Layout) => gridItem.i === dashboardGridItem
					);

					if (!gridItemToBeChanged || gridItemToBeChanged.h === newElementHValue)
						return previousNonMobileLayout;

					gridItemToBeChanged.h = newElementHValue;

					return newNonMobileGridConfig;
				});
			});

			for (const index in dashboardGridItemChildrenToBeObserved) {
				const dashboardItemToBeObserved = dashboardGridItemChildrenToBeObserved.item(Number(index));
				if (dashboardItemToBeObserved) {
					resizeObserver.observe(dashboardItemToBeObserved);
				}
			}

			return () => resizeObserver.disconnect();
		}
	}, [dashboardGridItemChildrenToBeObserved, setNonMobileLayout, isMobile, isEditMode, dashboardGridItem]);

	const gridItemZIndex = getGridItemZIndex();

	const onMouseEnter = () => {
		if (isEditMode) {
			setCursorType("grab");
		}
	};

	const onMouseLeave = () => {
		setCursorType("default");
	};

	const onCustomMouseDown = (e: MouseEvent<HTMLDivElement>) => {
		if (isEditMode) {
			setCursorType("grabbing");
		}

		if (onMouseDown) {
			onMouseDown(e);
		}
	};

	const onCustomMouseUp = (e: MouseEvent<HTMLDivElement>) => {
		if (isEditMode) {
			setCursorType("grab");
		}

		if (onMouseUp) {
			onMouseUp(e);
		}
	};

	return { gridItemZIndex, cursorType, onMouseEnter, onMouseLeave, onCustomMouseDown, onCustomMouseUp };
};
